import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n";

export default function Header() {
  const { lang, toggleLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), testid: "nav-home" },
    { to: "/services", label: t("nav.services"), testid: "nav-services" },
    { to: "/about", label: t("nav.about"), testid: "nav-about" },
    { to: "/contact", label: t("nav.contact"), testid: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-colors ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-stone-200" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_gsm-service-hub-2/artifacts/2b7tqu2l_D%C3%A9m%C3%A9nagement%20%20Am%C3%A9nagement%20R%C3%A9sidentiel%20Commercial%20%20Industriel%20Nettoyage%20apr%C3%A8s%20construction%20Sous-%20traitance%20accept%C3%A9%20%284%29.png"
            alt="GSM Service Maintenance"
            className="w-14 h-14 md:w-16 md:h-16 object-contain"
          />
          <div className="leading-tight">
            <div className="font-serif text-xl md:text-2xl text-stone-900">GSM Service</div>
            <div className="text-[11px] md:text-xs tracking-[0.22em] uppercase text-stone-500 font-mono">Maintenance</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-[#B95736]" : "text-stone-700 hover:text-stone-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="hidden sm:inline-flex items-center gap-1 text-xs font-mono tracking-widest border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
            aria-label="Toggle language"
          >
            <span className={lang === "fr" ? "text-[#B95736]" : ""}>FR</span>
            <span className="text-stone-400">/</span>
            <span className={lang === "en" ? "text-[#B95736]" : ""}>EN</span>
          </button>

          <Link to="/contact" data-testid="nav-cta-quote" className="hidden md:inline-flex btn-primary">
            {t("nav.quote")}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-stone-200" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`m-${l.testid}`}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-lg font-serif ${isActive ? "text-[#B95736]" : "text-stone-800"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              data-testid="m-lang-toggle"
              onClick={toggleLang}
              className="mt-2 text-xs font-mono tracking-widest border border-stone-300 rounded-full px-3 py-1.5 w-fit"
            >
              <span className={lang === "fr" ? "text-[#B95736]" : ""}>FR</span>
              <span className="text-stone-400 mx-1">/</span>
              <span className={lang === "en" ? "text-[#B95736]" : ""}>EN</span>
            </button>
            <Link to="/contact" data-testid="m-nav-cta-quote" className="btn-primary w-fit mt-2">
              {t("nav.quote")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
